import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Clock } from 'lucide-react';
import api from '../lib/api';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const { cart, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reserving, setReserving] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes in seconds
  const [shippingInfo, setShippingInfo] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'United States'
  });

  useEffect(() => {
    if (!cart?.items?.length) {
      navigate('/cart');
      return;
    }
    
    // Reserve products
    reserveProducts();
  }, [cart, navigate]);

  useEffect(() => {
    if (!reserving && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            toast.error('Reservation expired. Redirecting to cart...');
            setTimeout(() => navigate('/cart'), 2000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [reserving, timeRemaining, navigate]);

  const reserveProducts = async () => {
    try {
      // Reserve all products in cart
      await api.post('/cart/reserve');
      toast.success('Products reserved for 5 minutes');
      setReserving(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reserve products');
      navigate('/cart');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (e) => {
    setShippingInfo({ ...shippingInfo, [e.target.name]: e.target.value });
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      // Create Stripe checkout session
      const response = await api.post('/checkout/create-session', {
        shipping_info: shippingInfo
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  if (!cart?.items?.length) return null;

  if (reserving) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p>Reserving products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Timer Warning */}
        <div className={`mb-6 p-4 rounded-lg border-2 flex items-center gap-3 ${
          timeRemaining < 60 ? 'bg-red-50 border-red-500' : 'bg-yellow-50 border-yellow-500'
        }`}>
          <Clock className={`w-6 h-6 ${timeRemaining < 60 ? 'text-red-600' : 'text-yellow-600'}`} />
          <div className="flex-1">
            <p className={`font-bold ${timeRemaining < 60 ? 'text-red-900' : 'text-yellow-900'}`}>
              Time Remaining: {formatTime(timeRemaining)}
            </p>
            <p className={`text-sm ${timeRemaining < 60 ? 'text-red-700' : 'text-yellow-700'}`}>
              Complete your purchase within {Math.ceil(timeRemaining / 60)} minute{timeRemaining > 60 ? 's' : ''} or your reservation will expire
            </p>
          </div>
        </div>

        <h1 className="font-serif text-4xl mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Shipping Information */}
          <div>
            <h2 className="font-serif text-2xl mb-6">Shipping Information</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={shippingInfo.full_name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={shippingInfo.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={shippingInfo.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="address_line1">Address Line 1 *</Label>
                <Input
                  id="address_line1"
                  name="address_line1"
                  value={shippingInfo.address_line1}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="address_line2">Address Line 2</Label>
                <Input
                  id="address_line2"
                  name="address_line2"
                  value={shippingInfo.address_line2}
                  onChange={handleInputChange}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={shippingInfo.city}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    name="state"
                    value={shippingInfo.state}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="postal_code">Postal Code *</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    value={shippingInfo.postal_code}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Input
                    id="country"
                    name="country"
                    value={shippingInfo.country}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <h2 className="font-serif text-2xl mb-6">Order Summary</h2>
            <div className="border border-gray-200 p-6">
              <div className="space-y-4 mb-6">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex gap-4">
                    <img
                      src={item.main_image_url || 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=100&q=80'}
                      alt={item.product_name}
                      className="w-16 h-16 object-cover"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium">{formatPrice(item.price_at_time * item.quantity)}</p>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-xl font-serif">
                  <span>Total</span>
                  <span>{formatPrice(cart.subtotal)}</span>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full mt-6 bg-black text-white hover:bg-gray-900"
                size="lg"
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Secure payment powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
